import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, ChevronDown, ChevronUp } from "lucide-react";

const TABS = [
  { key: "pending", label: "Bekleyen" },
  { key: "approved", label: "Onaylanan" },
  { key: "rejected", label: "Reddedilen" },
];

export default function AdminTeacherApplications() {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = async (status = tab) => {
    const { data } = await api.get("/admin/teacher-applications", { params: { status } });
    setItems(data);
  };
  useEffect(() => { load(tab); setOpenId(null); /* eslint-disable-next-line */ }, [tab]);

  const decide = async (app, action) => {
    const verb = action === "approve" ? "onaylamak" : "reddetmek";
    if (!window.confirm(`"${app.full_name}" başvurusunu ${verb} istediğinize emin misiniz?`)) return;
    setBusyId(app.application_id);
    try {
      await api.post(`/admin/teacher-applications/${app.application_id}/${action}`, action === "reject" ? {} : undefined);
      toast.success(action === "approve" ? "Başvuru onaylandı, öğretmen yetkisi verildi." : "Başvuru reddedildi.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İşlem başarısız");
    } finally { setBusyId(null); }
  };

  return (
    <div className="space-y-4" data-testid="admin-teacher-apps-page">
      <div>
        <h1 className="font-display text-3xl tracking-tight">Öğretmen Başvuruları</h1>
        <p className="text-sm text-slate-400 mt-1">
          Onaylanan başvurular mevcut kullanıcı hesabına öğretmen yetkisi verir; ayrı hesap oluşturulmaz.
        </p>
      </div>

      <div className="flex gap-1 border-b border-[#262626]" data-testid="admin-teacher-apps-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            data-testid={`admin-teacher-apps-tab-${t.key}`}
            className={`px-4 py-2 text-sm rounded-t-md transition-colors ${
              tab === t.key ? "bg-[#EA580C] text-white" : "text-slate-300 hover:bg-[#171717]"
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {items.map((a) => {
          const open = openId === a.application_id;
          return (
            <div key={a.application_id}
                 data-testid={`admin-teacher-app-row-${a.application_id}`}
                 className="border border-[#262626] rounded-lg overflow-hidden">
              <button onClick={() => setOpenId(open ? null : a.application_id)}
                data-testid={`admin-teacher-app-toggle-${a.application_id}`}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#171717]">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{a.full_name}</div>
                  <div className="text-xs text-slate-500 truncate">{a.email}</div>
                </div>
                <div className="text-xs text-slate-500 font-mono">
                  {new Date(a.created_at).toLocaleDateString("tr-TR")}
                </div>
                {open ? <ChevronUp size={14} className="text-slate-500" /> : <ChevronDown size={14} className="text-slate-500" />}
              </button>

              {open && (
                <div className="px-4 pb-4 pt-1 border-t border-[#262626] space-y-3"
                     data-testid={`admin-teacher-app-detail-${a.application_id}`}>
                  <Detail label="Ad Soyad" value={a.full_name} />
                  <Detail label="E-posta" value={a.email} />
                  <Detail label="Telefon" value={a.phone} />
                  <Detail label="Açıklama" value={a.description} />
                  <Detail label="Başvuru Tarihi" value={new Date(a.created_at).toLocaleString("tr-TR")} />
                  {a.reviewed_at && (
                    <>
                      <Detail label="Karar Tarihi" value={new Date(a.reviewed_at).toLocaleString("tr-TR")} />
                      <Detail label="Karar Veren" value={a.reviewed_by_email || a.reviewed_by} />
                      {a.review_note && <Detail label="Not" value={a.review_note} />}
                    </>
                  )}
                  {a.status === "pending" && (
                    <div className="flex gap-2 pt-1">
                      <button onClick={() => decide(a, "approve")}
                        disabled={busyId === a.application_id}
                        data-testid={`admin-teacher-app-approve-${a.application_id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-sm disabled:opacity-50">
                        <CheckCircle2 size={14} /> Onayla
                      </button>
                      <button onClick={() => decide(a, "reject")}
                        disabled={busyId === a.application_id}
                        data-testid={`admin-teacher-app-reject-${a.application_id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-rose-500/40 text-rose-300 hover:bg-rose-500/10 text-sm disabled:opacity-50">
                        <XCircle size={14} /> Reddet
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {items.length === 0 && (
          <div className="border border-[#262626] rounded-lg p-8 text-center text-slate-500"
               data-testid="admin-teacher-apps-empty">
            Bu durumda başvuru yok.
          </div>
        )}
      </div>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div className="grid sm:grid-cols-[140px_1fr] gap-1 text-sm">
      <div className="text-slate-500 text-xs pt-0.5">{label}</div>
      <div className="text-slate-200 whitespace-pre-wrap break-words">{value || "—"}</div>
    </div>
  );
}
